import { z } from "zod";

/**
 * Eigen huisstijl van een organisatie voor de PDF-rapporten (docs/BRAND.md): logo en accentkleur.
 * De app zelf blijft Klantpunt.
 */
export const REPORT_LOGO_MIMES = ["image/png", "image/jpeg"] as const;
/** Lijnen, labels en accenten staan op wit: minimaal 3:1 (WCAG voor grafische elementen). */
export const REPORT_ACCENT_MIN_CONTRAST = 3;

const hex = (s: string) => /^#[0-9a-fA-F]{6}$/.test(s);

/** Relatieve luminantie volgens WCAG 2.x. */
export function luminance(color: string): number {
  const n = parseInt(color.slice(1), 16);
  const ch = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * ch[0]! + 0.7152 * ch[1]! + 0.0722 * ch[2]!;
}

export function contrastRatio(a: string, b: string): number {
  const [x, y] = [luminance(a), luminance(b)].sort((p, q) => q - p) as [number, number];
  return (x + 0.05) / (y + 0.05);
}

export const accentSchema = z
  .string()
  .trim()
  .refine(hex, "Kies een kleur als #RRGGBB")
  .transform((s) => s.toUpperCase())
  .refine(
    (s) => contrastRatio(s, "#FFFFFF") >= REPORT_ACCENT_MIN_CONTRAST,
    "Deze kleur is te licht voor tekst en lijnen op wit; kies een donkerdere tint",
  );

export const reportStyleSchema = z.object({
  accent: accentSchema.nullable(),
  logoFileId: z.uuid().nullable(),
  /** Vierkant pictogram voor kleine plekken in de app; weggelaten = ongewijzigd. */
  iconFileId: z.uuid().nullable().optional(),
});
export type ReportStyleBody = z.input<typeof reportStyleSchema>;
export interface ReportStyleDto {
  accent: string | null;
  logoFileId: string | null;
  iconFileId?: string | null;
}
